/**
 * 세션 Bill 조회 훅 — 분할결제 (menu #814 / admin #2204)
 *
 * ## 403 은 오류가 아니라 상태다
 *
 * 분할결제는 **매장 정책 AND 개발 게이트**로 열린다(admin #2211). 둘 중 하나라도 닫혀
 * 있으면 `GET /bills` 가 403 이다.
 *
 * 그것을 "실패" 로 다루면 켜지 않은 매장의 손님에게 오류가 보인다 — 그 매장은 분할결제를
 * 쓰지 않기로 한 것이지 무언가 잘못된 것이 아니다.
 *
 * → `isSplitAvailable=false` 로 환원하고, 화면은 **아무것도 그리지 않는다.**
 *   기존 UX 무변화 원칙(#814)이 이렇게 지켜진다.
 *
 * ## 409 는 남이 먼저 가져갔다는 뜻이다
 *
 * 지분 claim 은 여러 손님이 같은 항목을 동시에 노린다. 서버는 낙관적 락으로 한 명만
 * 통과시키고 나머지에게 409 를 준다.
 *
 * **조용히 재시도하면 안 된다** — 재시도가 성공하는 순간 손님은 자기가 고르지 않은
 * 상태를 받는다. 최신 상태를 다시 읽어서 보여주고, 다시 고르게 한다.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  splitPaymentApi,
  type ClaimShareRequest,
  type SessionBillsResponse,
} from '@/lib/api/split-payment';
import { logger } from '@/utils/logger';
import { SESSION_BILLS_CHANGED_EVENT } from '@/lib/sessionStorageKeys';

export interface UseSessionBillsReturn {
  data: SessionBillsResponse | null;
  /** 이 매장에서 분할결제가 열려 있는가. 403 이면 false. */
  isSplitAvailable: boolean;
  isLoading: boolean;
  hasLoaded: boolean;
  /** 분할이 꺼진 것(403)은 여기 담기지 않는다 — 그건 오류가 아니다. */
  error: string | null;
  refresh: () => Promise<void>;
  /** 지분 가져오기. 성공하면 목록을 다시 읽는다. */
  claim: (request: ClaimShareRequest) => Promise<ClaimOutcome>;
  isClaiming: boolean;
}

/**
 * claim 결과.
 *
 * `conflict` 는 실패가 아니라 **경합**이다 — 화면은 오류가 아니라 "누가 먼저
 * 가져갔습니다, 다시 골라 주세요" 를 말해야 한다.
 */
export type ClaimOutcome = 'ok' | 'conflict' | 'error';

function statusOf(error: unknown): number | null {
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const status = (error as { status: unknown }).status;

    return typeof status === 'number' ? status : null;
  }

  return null;
}

export function useSessionBills(
  sessionToken: string | null,
  deviceToken: string | null
): UseSessionBillsReturn {
  const [data, setData] = useState<SessionBillsResponse | null>(null);
  // 확인되기 전에는 **닫힌 것으로 본다** — 열려 있다고 가정하고 UI 를 그리면,
  // 꺼진 매장에서 잠깐 분할 화면이 보였다 사라진다.
  const [isSplitAvailable, setIsSplitAvailable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const eventSourceRef = useRef<object>({});

  const load = useCallback(async (): Promise<void> => {
    if (sessionToken === null || deviceToken === null) {
      setData(null);
      setIsSplitAvailable(false);
      setHasLoaded(true);

      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await splitPaymentApi.listBills(sessionToken, deviceToken);

      // OFF 뒤에도 이미 생긴 ledger는 200/read-only로 내려온다. data 가 보인다는 사실과
      // mutation 가능 여부를 섞으면 kill-switch 상태에서 claim UI가 다시 열린다.
      setData(response);
      setIsSplitAvailable(response.split_payment_available);
    } catch (caught) {
      const status = statusOf(caught);

      if (status === 403) {
        // 매장이 분할결제를 쓰지 않는다. 정상 상태다.
        setData(null);
        setIsSplitAvailable(false);

        return;
      }

      logger.error('[useSessionBills] 조회 실패', caught);
      setData(null);
      setIsSplitAvailable(false);
      setError(caught instanceof Error ? caught.message : 'unknown');
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
    }
  }, [sessionToken, deviceToken]);

  const claim = useCallback(
    async (request: ClaimShareRequest): Promise<ClaimOutcome> => {
      if (sessionToken === null || deviceToken === null) {
        return 'error';
      }

      setIsClaiming(true);

      try {
        await splitPaymentApi.claim(sessionToken, deviceToken, request);
        await load();
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent(SESSION_BILLS_CHANGED_EVENT, {
            detail: eventSourceRef.current,
          }));
        }

        return 'ok';
      } catch (caught) {
        // 경합이든 실패든 서버가 진실이다 — 화면의 낡은 지분 표시를 그대로 두면
        // 손님이 이미 없어진 항목을 계속 누른다.
        await load();

        if (statusOf(caught) === 409) {
          return 'conflict';
        }

        logger.error('[useSessionBills] claim 실패', caught);

        return 'error';
      } finally {
        setIsClaiming(false);
      }
    },
    [sessionToken, deviceToken, load]
  );

  useEffect(() => {
    setHasLoaded(false);
    void load();
  }, [load]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const refresh = (event: Event): void => {
      if (
        event instanceof CustomEvent
        && event.detail === eventSourceRef.current
      ) return;
      void load();
    };
    window.addEventListener(SESSION_BILLS_CHANGED_EVENT, refresh);
    return () => { window.removeEventListener(SESSION_BILLS_CHANGED_EVENT, refresh); };
  }, [load]);

  return {
    data,
    isSplitAvailable,
    isLoading,
    hasLoaded,
    error,
    refresh: load,
    claim,
    isClaiming,
  };
}
